import {
  REEBELO_CURRENCY,
  ReebeloConditions,
  TagHelpers,
} from '@lambda/reebelo';
import { ProductVariant } from '@lambda/commons/apis/shopify/types/storefront';
import { CartLineT, NoteT } from '@lambda/apis/src/cart/types';
import { OfferT, ProductT, SkuT } from '@lambda/apis/src/catalog-v2/types';
import settings from '@/settings';
import {
  ProductEventProperties,
  ProductTrackRequest,
  ReebeloCareTrackRequest,
} from './types';
import { ProductDetails } from '@/components/products/PurchaseLinks';
import { SegmentEvent } from './events';
import {
  getAnalytics,
  shouldTrack,
  trackEvent,
} from './vendor/segment/segment';
import { logger } from '../logger';
import { generateCarouselImages } from '@/components/v2/product/sections/main/left/image/helpers';

const getProductUrl = (): string => {
  try {
    return window.location.href;
  } catch (e) {
    return '';
  }
};

const makeFromCartLine = (lineItem: CartLineT): ProductEventProperties => {
  const category = lineItem.category?.name;

  const currency = REEBELO_CURRENCY[settings.store];

  const price = lineItem.price / 100;

  const eventPayload: ProductEventProperties = {
    line_item_id: lineItem.id,
    offer_pk: lineItem.typeId,
    vendor: lineItem.vendor,
    sku: lineItem.sku,
    psku: lineItem.psku,
    name: lineItem.title,
    category,
    brand: lineItem.make,
    color: lineItem.variants?.find((v) => v.name === 'Color')?.value || '',
    condition:
      (lineItem.variants?.find((v) => v.name === 'Condition')?.value as
        | ReebeloConditions
        | undefined) || ReebeloConditions.Good,
    price,
    compare_price: lineItem.compareAtPrice
      ? lineItem.compareAtPrice / 100
      : undefined,
    currency,
    quantity: 1,
    images: lineItem.image ? [lineItem.image] : undefined,
    store: settings.store,
    url: getProductUrl(),
    timestamp: new Date().toISOString(),
    battery: {
      upgrade_pct: lineItem.notes
        ? (() => {
            const note = lineItem.notes.find(
              (n) => n.name === 'batteryUpgradePct',
            );

            return note && note.value !== undefined
              ? Number(note.value)
              : undefined;
          })()
        : undefined,
      upgrade_price: lineItem.notes
        ? (() => {
            const note = lineItem.notes.find(
              (n) => n.name === 'batteryUpgradePrice',
            );

            return note && note.value !== undefined
              ? Number(note.value)
              : undefined;
          })()
        : undefined,
    },
  };

  return eventPayload;
};

//  MsCatalog2
const makeFromMsCatalog = (v2: {
  sku: SkuT | null;
  product: ProductT;
  offer: OfferT | null;
  isOutOfStock: boolean;
  slotNumber?: number;
}): ProductEventProperties => {
  const { sku, product, offer, isOutOfStock, slotNumber } = v2;

  const currency = REEBELO_CURRENCY[settings.store];

  const price = offer?.price ? offer.price / 100 : undefined;
  const compareAtPrice = sku?.compareAtPrice
    ? sku.compareAtPrice / 100
    : undefined;

  const images =
    generateCarouselImages(sku, product, isOutOfStock)?.map((img) => img.url) ??
    [];

  const eventPayload: ProductEventProperties = {
    product_id: product.id,
    sku_id: sku?.id,
    offer_id: offer?.id,
    vendor: offer?.vendor?.name ?? '',
    sku: offer?.vendorSku,
    psku: product.psku,
    name: product?.name ?? '',
    category: product.category?.name,
    brand: product.brand?.name,
    color: sku?.variants?.find((v) => v.variant.slug === 'color')?.value?.value,
    condition: sku?.variants?.find((v) => v.variant.slug === 'condition')?.value
      ?.value as ReebeloConditions,
    price,
    compare_price: compareAtPrice,
    currency,
    quantity: 1,
    images,
    store: settings.store,
    url: getProductUrl(),
    timestamp: new Date().toISOString(),
    slot_number: slotNumber ?? 0,
  };

  return eventPayload;
};

const makeFromProductDetails = (
  details: ProductDetails,
): ProductEventProperties => {
  const currency = REEBELO_CURRENCY[settings.store];

  const condition =
    (details.condition as ReebeloConditions) || ReebeloConditions.Good;

  const price = parseFloat(details.price);

  const eventPayload: ProductEventProperties = {
    variant_id: details.id,
    sku: details.sku || '',
    vendor: details.vendor,
    psku: details.psku || '',
    name: details.name,
    category: details.category,
    brand: details.brand,
    color: details.color || '',
    condition,
    price,
    compare_price: Number.isNaN(details.comparePrice)
      ? undefined
      : details.comparePrice,
    currency,
    quantity: 1,
    images: details?.images?.map((img) => img.url),
    store: settings.store,
    url: getProductUrl(),
    timestamp: new Date().toISOString(),
  };

  return eventPayload;
};

const makeFromProductVariant = (
  variant: ProductVariant,
  variantNotes?: NoteT[],
): ProductEventProperties => {
  const currency = REEBELO_CURRENCY[settings.store];
  const { product } = variant;
  const tags = TagHelpers.extractTags(product.tags);
  let images: string[] = [];

  if (product.images?.edges.length) {
    images = product.images.edges.map((n) => n.node.url);
  } else if (variant.image) {
    images = [variant.image.url];
  }

  const eventPayload: ProductEventProperties = {
    product_id: product.id,
    variant_id: variant.id,
    sku: variant.sku,
    vendor: product.vendor,
    psku: tags.psku,
    name: product.title.split(' - ')[0],
    category: tags.category,
    brand: tags.brand,
    color: tags.color,
    condition: tags.condition,
    price: Number(variant.priceV2.amount),
    compare_price: Number(variant.compareAtPriceV2?.amount),
    currency,
    quantity: 1,
    images,
    store: settings.store,
    url: product.onlineStoreUrl,
    timestamp: new Date().toISOString(),
    battery: {
      upgrade_pct: Number(
        variantNotes?.find((note) => note.name === '_battery_upgrade_pct')
          ?.value,
      ),
      upgrade_price: Number(
        variantNotes?.find((note) => note.name === '_battery_upgrade_price')
          ?.value,
      ),
    },
  };

  return eventPayload;
};

export const makeProductProperties = (
  r: ProductTrackRequest,
): ProductEventProperties => {
  if (r.v2) {
    return makeFromMsCatalog(r.v2);
  }

  if (r.cartLine) {
    return makeFromCartLine(r.cartLine);
  }

  if (r.details) {
    return makeFromProductDetails(r.details);
  }

  if (r.variant) {
    return makeFromProductVariant(r.variant, r.variantNotes);
  }

  throw new Error(`unsupported product object given for tracking: ${r}`);
};

const trackProductEvent = (
  event: SegmentEvent,
  r: ProductTrackRequest,
): ProductEventProperties | null => {
  try {
    logger.debug({ event, r }, 'tracking product event');

    const properties = { ...makeProductProperties(r), ...r.properties };

    trackEvent(event, properties);

    return properties;
  } catch (error: any) {
    logger.error({ event, r, error }, 'error tracking product event');

    return null;
  }
};

export const trackViewed = (r: ProductTrackRequest): void => {
  if (!shouldTrack()) {
    logger.info({ r }, 'not tracking product viewed in segment');

    return;
  }

  const properties = trackProductEvent(SegmentEvent.PRODUCT_VIEWED, r);

  if (!properties) {
    return;
  }

  if (!r.customer) {
    return;
  }

  getAnalytics()?.identify({
    first_name: r.customer.firstName,
    last_name: r.customer.lastName,
    email: r.customer.email,
    last_viewed: properties,
  });
};

export const trackAddToCart = (r: ProductTrackRequest): void => {
  trackProductEvent(SegmentEvent.PRODUCT_ADDED, r);
};

export const trackRemoveFromCart = (r: ProductTrackRequest): void => {
  trackProductEvent(SegmentEvent.PRODUCT_REMOVED, r);
};

export const trackProductsSearched = (query: string): void => {
  trackEvent(SegmentEvent.PRODUCTS_SEARCHED, { query });
};

const trackReebeloCaretEvent = (
  event: SegmentEvent,
  props: ReebeloCareTrackRequest,
): ReebeloCareTrackRequest | null => {
  try {
    logger.debug({ event, props }, 'tracking reebelocare event');

    trackEvent(event, props);

    return props;
  } catch (error: any) {
    logger.error({ event, props, error }, 'error tracking reebelocare event');

    return null;
  }
};

export const trackReebeloCareSelected = (props: ReebeloCareTrackRequest) =>
  trackReebeloCaretEvent(SegmentEvent.REEBELOCARE_SELECTED, props);

export const trackReebeloCareDeselected = (props: ReebeloCareTrackRequest) =>
  trackReebeloCaretEvent(SegmentEvent.REEBELOCARE_DESELECTED, props);

export const trackReebeloCareAdded = (props: ReebeloCareTrackRequest) =>
  trackReebeloCaretEvent(SegmentEvent.REEBELOCARE_ADDED, props);

export const trackReebeloCareRemoved = (props: ReebeloCareTrackRequest) =>
  trackReebeloCaretEvent(SegmentEvent.REEBELOCARE_REMOVED, props);
